import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import socialPreview from '../data/socialPreview.json';

/**
 * Длительность курса в формате ISO 8601 — только его понимает Google.
 * "7 часов видео" → PT7H, "2 месяца обучения" → P2M.
 */
const toIsoDuration = (course) => {
  if (course?.totalHours) return `PT${Math.round(course.totalHours)}H`;

  const raw = course?.duration || '';
  const months = raw.match(/(\d+)\s*месяц/i);
  if (months) return `P${months[1]}M`;
  const hours = raw.match(/(\d+)\s*час/i);
  if (hours) return `PT${hours[1]}H`;
  return undefined;
};

/**
 * Реальное число отзывов — сумма по площадкам. Раньше сюда подставлялось
 * количество студентов, что для Google является недостоверной разметкой.
 */
const countReviews = (course) => {
  const platforms = course?.reviewPlatforms;
  const fromPlatforms = platforms
    ? Object.values(platforms).reduce((sum, p) => sum + (p?.count || 0), 0)
    : 0;
  return fromPlatforms || course?.testimonials?.length || 0;
};

const SEO = ({
  title,
  description,
  image,
  url,
  type = 'website',
  course = null,
  exactDescription = false,
  noindex = false
}) => {
  const siteName = 'Fokeev';
  const authorName = 'Дмитрий Фокеев';
  const defaultTitle = 'Fokeev — Онлайн-курсы по дизайну и программированию от Дмитрия Фокеева';
  const defaultDescription = 'Обучение дизайну, программированию и нейросетям с нуля до профессионала от Дмитрия Фокеева. Figma, Photoshop, JavaScript, React, AI и многое другое. Пожизненный доступ и поддержка преподавателя.';
  const defaultImage = socialPreview.image;
  const baseUrl = 'https://dmitrii-fokeev.ru';

  const seoTitle = title ? `${title} | ${siteName} — ${authorName}` : defaultTitle;
  const seoDescription = exactDescription ? description : description ? `${description} Автор курса: ${authorName}.` : defaultDescription;
  const seoUrl = url ? `${baseUrl}${url}` : baseUrl;
  // og:image обязан быть абсолютным URL, иначе превью не подтянется в соцсетях
  const rawImage = image || defaultImage;
  const seoImage = rawImage.startsWith('http') ? rawImage : `${baseUrl}${rawImage}`;

  const usesBrandImage = rawImage === defaultImage;
  const isBrandPage = !url || url === '/' || url === '/courses';
  const shareTitle = isBrandPage ? (url === '/courses' ? socialPreview.catalogTitle : socialPreview.title) : seoTitle;
  const shareDescription = isBrandPage ? socialPreview.description : seoDescription;
  const imageAlt = course?.title || (usesBrandImage ? socialPreview.alt : seoTitle);

  // Статические теги из index.html — запасной вариант для краулеров без JS.
  // После старта приложения снимаем их, иначе на странице будет по два
  // title/description/canonical, а конфликтующие canonical поисковики игнорируют.
  useEffect(() => {
    document
      .querySelectorAll('[data-default-seo]')
      .forEach((el) => el.remove());
  }, []);

  // Generate course structured data if course is provided
  const reviewCount = countReviews(course);
  const isoDuration = toIsoDuration(course);

  const courseSchema = course ? {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": course.title,
    "description": course.description,
    "url": seoUrl,
    "image": seoImage,
    "inLanguage": "ru-RU",
    "provider": {
      "@type": "Organization",
      "name": "Fokeev",
      "url": "https://dmitrii-fokeev.ru"
    },
    "creator": {
      "@type": "Person",
      "name": "Дмитрий Фокеев",
      "url": "https://dmitrii-fokeev.ru"
    },
    "offers": {
      "@type": "Offer",
      "price": course.price,
      "priceCurrency": "RUB",
      "availability": "https://schema.org/InStock",
      "url": seoUrl
    },
    "hasCourseInstance": {
      "@type": "CourseInstance",
      "courseMode": "online",
      ...(isoDuration ? { "courseWorkload": isoDuration } : {})
    },
    // Рейтинг показываем, только если отзывы действительно есть
    "aggregateRating": course.rating && reviewCount ? {
      "@type": "AggregateRating",
      "ratingValue": course.rating,
      "reviewCount": reviewCount,
      "bestRating": 5,
      "worstRating": 1
    } : undefined
  } : null;

  // Хлебные крошки — расширенный сниппет с навигацией в выдаче
  const breadcrumbSchema = course ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Главная", "item": baseUrl },
      { "@type": "ListItem", "position": 2, "name": "Курсы", "item": `${baseUrl}/courses` },
      { "@type": "ListItem", "position": 3, "name": course.title, "item": seoUrl }
    ]
  } : null;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{seoTitle}</title>
      <meta name="title" content={seoTitle} />
      <meta name="description" content={seoDescription} />
      <meta name="author" content={authorName} />

      {/* Служебные и несуществующие страницы не должны попадать в индекс */}
      <meta name="robots" content={noindex ? 'noindex, follow' : 'index, follow'} />

      {/* Canonical URL */}
      {!noindex && <link rel="canonical" href={seoUrl} />}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={seoUrl} />
      <meta property="og:title" content={shareTitle} />
      <meta property="og:description" content={shareDescription} />
      <meta property="og:image" content={seoImage} />
      <meta property="og:image:secure_url" content={seoImage} />
      <meta property="og:image:alt" content={imageAlt} />
      {usesBrandImage && <meta property="og:image:width" content={String(socialPreview.width)} />}
      {usesBrandImage && <meta property="og:image:height" content={String(socialPreview.height)} />}
      {usesBrandImage && <meta property="og:image:type" content={socialPreview.type} />}
      <meta property="og:locale" content="ru_RU" />
      <meta property="og:site_name" content={siteName} />
      <meta property="article:author" content={authorName} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={seoUrl} />
      <meta name="twitter:title" content={shareTitle} />
      <meta name="twitter:description" content={shareDescription} />
      <meta name="twitter:image" content={seoImage} />
      <meta name="twitter:image:alt" content={imageAlt} />
      
      {/* Course Structured Data */}
      {courseSchema && (
        <script type="application/ld+json">
          {JSON.stringify(courseSchema)}
        </script>
      )}

      {/* Breadcrumbs */}
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
